/**
 * Tope de peso y tamaño para una imagen que el usuario SUBE al chat
 * (ADR-2026-09-14-el-modelo-ve-el-opt-y-el-error-habla-en-castellano).
 *
 * Las subidas del chat (`agent-refs`) no tienen `opt/`: lo que se sube es lo que el modelo
 * va a intentar bajar por URL. Dos PNG de 1,6 y 1,9 MB alcanzaron para que Anthropic
 * cortara con "timed out while trying to download the file". Acá se acota ANTES de subir:
 * lado largo ≤ 2000 y, si aun así pesa más de 1,5 MB, se re-encoda a JPEG. La misma imagen
 * va después como `reference_image` a la generación; 2000 px es de sobra para eso.
 *
 * Por qué 2000 y no 2048 (el tope del backend para visión): Anthropic aplica un límite
 * más estricto a CADA imagen cuando el pedido trae más de 20 bloques `image` —contando
 * todo el historial y lo que hay adentro de los `tool_result`— y rechaza las que pasan
 * los 2000 px de lado. Un chat con `ver_productos` y un moodboard llega a 20 sin
 * esfuerzo, y un 2048 quedaba 48 px arriba del corte.
 *
 * El 1,5 MB es el DISPARADOR de la re-encodación, no un tope sobre el resultado: un JPEG
 * q0,85 de 2000 px queda en ~0,4–1 MB y no se vuelve a medir. Es por imagen, al subir.
 * Arrancó en 1 MB; Mati lo subió a 1,5, que es el máximo que todavía atrapa los dos PNG
 * del caso real (1,56 y 1,90 MB). Subirlo más deja pasar el primero tal cual.
 */

export const LADO_MAX_ADJUNTO = 2000;
export const PESO_MAX_ADJUNTO = 1.5 * 1024 * 1024;
const CALIDAD_JPEG = 0.85;

export interface Medidas {
  ancho: number;
  alto: number;
  bytes: number;
}

export interface PlanDeAligerado {
  /** `false`: se sube tal cual. `true`: se re-encoda a JPEG con `escala`. */
  reencodar: boolean;
  /** Factor sobre ancho y alto (≤ 1). Con `reencodar: false` es 1. */
  escala: number;
}

/** La decisión, pura: qué hacer con una imagen de estas medidas. */
export function planDeAligerado(
  medidas: Medidas,
  limites: { ladoMax: number; pesoMax: number } = { ladoMax: LADO_MAX_ADJUNTO, pesoMax: PESO_MAX_ADJUNTO },
): PlanDeAligerado {
  const ladoLargo = Math.max(medidas.ancho, medidas.alto);
  const escala = ladoLargo > limites.ladoMax ? limites.ladoMax / ladoLargo : 1;
  const reencodar = escala < 1 || medidas.bytes > limites.pesoMax;
  return { reencodar, escala: reencodar ? escala : 1 };
}

const medir = (file: File): Promise<{ img: HTMLImageElement; url: string }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('no se pudo leer la imagen'));
    };
    img.src = url;
  });

/**
 * Devuelve el archivo listo para subir: el mismo si ya cumple, o un JPEG re-encodado.
 * Si algo falla (un formato que el canvas no dibuja, sin contexto 2D) devuelve el ORIGINAL:
 * esto acota, nunca bloquea una subida.
 */
export async function aligerarImagen(file: File): Promise<File> {
  let leida: { img: HTMLImageElement; url: string };
  try {
    leida = await medir(file);
  } catch {
    return file;
  }
  const { img, url } = leida;
  try {
    const plan = planDeAligerado({ ancho: img.naturalWidth, alto: img.naturalHeight, bytes: file.size });
    if (!plan.reencodar) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * plan.escala));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * plan.escala));
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // JPEG no tiene alfa: un PNG recortado sobre transparente saldría sobre negro. Blanco.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', CALIDAD_JPEG));
    if (!blob) return file;
    const nombre = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], nombre, { type: 'image/jpeg' });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}
